import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import ProductItem from "../../components/product/ProductItem";
import {
  MAX_COMPARE_PRODUCTS,
  getProductImageUrl,
  getVisibleAttributeKeys,
  readStoredCompareProducts,
  writeStoredCompareProducts,
} from "../../utils/productCompare";

/**
 * 渲染已选商品底部对比操作栏。
 * @param {{ selectedProducts: Array<Record<string, any>>, onRemove: (id: string) => void, onClear: () => void, onCompare: () => void }} props 组件属性
 * @returns {React.JSX.Element | null} 底部对比操作栏
 */
const CompareSelectionBar = ({ selectedProducts, onRemove, onClear, onCompare }) => {
  if (!selectedProducts.length) {
    return null;
  }

  const canCompare = selectedProducts.length >= 2;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white shadow-[0_-6px_20px_rgba(15,23,42,0.12)]">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 md:flex-row md:items-end md:justify-between">
        <div className="flex min-w-0 flex-1 items-end gap-3 overflow-x-auto pb-1">
          {selectedProducts.map((product) => (
            <div key={product?._id} className="relative h-[58px] w-[58px] shrink-0 rounded border border-gray-200 bg-white p-1">
              <img
                src={getProductImageUrl(product)}
                alt={product?.name}
                className="h-full w-full object-contain"
              />
              <button
                type="button"
                onClick={() => onRemove(product?._id)}
                className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-red-500 bg-white text-sm leading-none text-red-600 hover:bg-red-50"
                aria-label={`Remove ${product?.name || "product"}`}
              >
                x
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 text-xs font-bold text-[#00144f] hover:text-[#2684fe]"
          >
            Clear all selections
          </button>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {!canCompare && (
            <span className="text-xs font-medium text-gray-500">Please select at least 2 products.</span>
          )}
          <button
            type="button"
            disabled={!canCompare}
            onClick={onCompare}
            className="min-h-[38px] min-w-[190px] rounded border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-[#00144f] transition-colors hover:border-[#2684fe] hover:text-[#2684fe] disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
          >
            Compare {selectedProducts.length} Products
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 渲染商品列表表格，固定基础列并追加商品动态属性列。
 * @param {{ products?: Array<Record<string, any>> }} props 组件属性
 * @returns {React.JSX.Element} 商品列表表格
 */
const ProductsTable = ({ products}) => {
  const navigate = useNavigate();
  const [selectedProducts, setSelectedProducts] = useState(() => readStoredCompareProducts());
  const attributeKeys = useMemo(() => getVisibleAttributeKeys(products), [products]);
  const selectedProductIds = useMemo(
    () => new Set(selectedProducts.map((product) => product?._id)),
    [selectedProducts]
  );
  const dynamicMinWidth = 976 + attributeKeys.length * 140;

  useEffect(() => {
    writeStoredCompareProducts(selectedProducts);
  }, [selectedProducts]);

  /**
   * 切换商品对比选择状态，并限制最大对比数量。
   * @param {Record<string, any>} product 商品对象
   */
  const handleToggleCompare = (product) => {
    if (!product?._id) return;

    setSelectedProducts((currentProducts) => {
      const isSelected = currentProducts.some((item) => item?._id === product._id);

      if (isSelected) {
        return currentProducts.filter((item) => item?._id !== product._id);
      }

      if (currentProducts.length >= MAX_COMPARE_PRODUCTS) {
        toast.error(`最多可对比 ${MAX_COMPARE_PRODUCTS} 个产品`);
        return currentProducts;
      }

      return [...currentProducts, product];
    });
  };

  /**
   * 从已选对比商品中移除指定商品。
   * @param {string} productId 商品 ID
   */
  const handleRemoveCompareProduct = (productId) => {
    setSelectedProducts((currentProducts) =>
      currentProducts.filter((product) => product?._id !== productId)
    );
  };

  /**
   * 跳转到商品对比页，并通过 URL 传递已选商品 ID。
   */
  const handleCompareProducts = () => {
    if (selectedProducts.length < 2) return;

    const ids = selectedProducts.map((product) => product?._id).filter(Boolean);
    writeStoredCompareProducts(selectedProducts);
    navigate(`/products/compare?ids=${ids.join(",")}`);
  };

  return (
    <>
      <div className="overflow-x-auto w-full pb-24">
        <table
          className="border-t-0 border-black border-solid md:border-t-4 border-[#011936] w-full"
          style={{ minWidth: `${dynamicMinWidth}px` }}
        >
          <thead className="md:table-header-group">
            <tr className="bg-white md:bg-[#E7E9EB] *:p-1 md:*:p-3 *:bg-inherit *:text-[#2684fe] md:*:text-[#2684fe] *:font-bold *:text-[10px] md:*:text-sm uppercase tracking-wider">
              <th className="sticky left-0 z-30 w-[42px] border-r-2 border-solid border-r-gray-400/20 text-center">
                Compare
              </th>
              <th className="sticky md:static left-[42px] z-20 bg-white md:bg-inherit w-[100px] md:w-[350px] border-r-2 border-solid border-r-gray-400/20 text-center shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] md:shadow-none">
                Product Details
              </th>
              <th className="border-r-2 border-solid border-r-gray-400/20 text-center">Datasheet</th>
              <th className="border-r-2 border-solid border-r-gray-400/20 text-center">Manufacturer</th>
              <th className="border-r-2 border-solid border-r-gray-400/20 text-center">Stock</th>
              <th className="min-w-[100px] whitespace-nowrap border-r-2 border-solid border-r-gray-400/20 text-center">
                Price ($)
              </th>
              <th className="border-r-2 border-solid border-r-gray-400/20 text-center min-w-[130px]">
                Order
              </th>
              <th className="border-r-2 border-solid border-r-gray-400/20 text-center">Creatyea Part No.</th>
              {attributeKeys.map((key) => (
                <th
                  key={key}
                  className="border-r-2 border-solid border-r-gray-400/20 text-center min-w-[140px] max-w-[220px]"
                  title={key}
                >
                  <span className="block truncate">{key}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products?.map((product) => {
              const isSelected = selectedProductIds.has(product?._id);

              return (
                <tr
                  key={product?._id}
                  className={`*:py-4 *:px-3 *:border-b border-gray-200 transition-colors duration-200 hover:bg-gray-50 ${
                    isSelected ? "bg-[#EAF2FF] ring-1 ring-inset ring-[#2684fe]" : "bg-white"
                  }`}
                >
                  <ProductItem
                    product={product}
                    attributeKeys={attributeKeys}
                    isSelected={isSelected}
                    onToggleCompare={handleToggleCompare}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <CompareSelectionBar
        selectedProducts={selectedProducts}
        onRemove={handleRemoveCompareProduct}
        onClear={() => setSelectedProducts([])}
        onCompare={handleCompareProducts}
      />
    </>
  );
};

export default ProductsTable;
